/**
 * CustomFieldValues — read-only display of a record's custom attributes as a proper
 * label / value table, not a cloud of `key: value` tags (tags read as secondary metadata;
 * these are real answers a person gave and deserve the same weight as the core fields).
 *
 * Labels come from the workspace's field definitions (so `travelDate` shows "Travel Date");
 * unknown keys fall back to a humanised key. Values are formatted by type — dates as dates,
 * true/false as Yes/No, numbers with separators. Fields that are defined but empty are left
 * out; fields not in the definitions (e.g. system-written chat signals) still show.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import { FieldDefinition } from '@/services/admin.api';

export interface AttributeEntry {
  key: string;
  value: unknown;
}

/** `travelDate` / `special_requirements` -> "Travel date" / "Special requirements". */
const humanise = (key: string): string => {
  const spaced = key
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const formatValue = (raw: unknown, def?: FieldDefinition): string => {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  const type = def?.type;
  if (type === 'boolean' || s === 'true' || s === 'false') {
    if (s === 'true') return 'Yes';
    if (s === 'false') return 'No';
  }
  if (type === 'date') {
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  }
  if (type === 'number') {
    const n = Number(s);
    if (Number.isFinite(n)) return n.toLocaleString();
  }
  return s;
};

interface Props {
  attributes?: AttributeEntry[] | undefined;
  definitions?: FieldDefinition[];
  /** Tighter rows, for nested use (e.g. inside an enquiry-history card). */
  dense?: boolean;
}

const CustomFieldValues: React.FC<Props> = ({ attributes, definitions = [], dense = false }) => {
  const byKey = new Map(definitions.map((d) => [d.key, d]));
  // Defined fields first, in the workspace's own order; anything else after, in stored order.
  const rank = (k: string) => (byKey.has(k) ? byKey.get(k)!.order : Number.MAX_SAFE_INTEGER);
  const rows = (attributes ?? [])
    .map((a, i) => ({ ...a, i, def: byKey.get(a.key) }))
    .filter((a) => formatValue(a.value, a.def) !== '')
    .sort((a, b) => rank(a.key) - rank(b.key) || a.i - b.i);

  if (rows.length === 0) return null;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
        columnGap: 3,
      }}
    >
      {rows.map((r) => (
        <Box
          key={r.key}
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(96px, 40%) 1fr',
            gap: 1.5,
            py: dense ? 0.6 : 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            alignItems: 'baseline',
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
            {r.def?.label ?? humanise(r.key)}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
            {formatValue(r.value, r.def)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default CustomFieldValues;
